import type { ReactNode } from 'react';
import React from 'react';
import { withTheme } from '@emotion/react';

import type { ThemeProps } from '../theme/theme';
import { isClient } from '../utils/isClient';

interface MediaProps {
  children: (matches: MatchesObject) => ReactNode | ReactNode[];
  queries: object;
  defaultMatches?: object;
}
export interface MatchesObject {
  tablet: boolean;
  desktop: boolean;
  large_desktop: boolean;
}
interface MediaState {
  matches: MatchesObject;
}
/**
 * This is mostly a full copy of react-media, with a fix for "two-pass" render issue:
 * https://github.com/ReactTraining/react-media/pull/96
 *
 * I also removed everything that is unneccesary in our application, which makes this lib
 * a little bit smaller
 *
 * At some point we all will use React hooks and this piece of code could be thrown away
 *
 * MIT License
 *
 * Copyright (c) 2016-2017 React Training
 *
 * https://github.com/ReactTraining/react-media
 */
class Media extends React.Component<MediaProps, MediaState> {
  queries: any[];

  constructor(props) {
    super(props);

    this.queries = [];
    this.updateMatches = this.updateMatches.bind(this);

    // This logic allows to avoid "two-pass render" in some
    // cases where defaultMatches are not used
    if (!isClient()) {
      // We are on the server, let's set some defaults
      this.state = {
        matches:
          props.defaultMatches ||
          Object.keys(this.props.queries).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
      };
    } else {
      // Init matchMedia listeners on client ...
      this.initialize();

      // ... and set them to state right away
      this.state = {
        matches: this.props.defaultMatches || this.getMatches(),
      };
    }
  }

  getMatches() {
    return this.queries.reduce((acc, { name, mqList }) => ({ ...acc, [name]: mqList.matches }), {});
  }

  updateMatches() {
    this.setState({ matches: this.getMatches() });
  }

  initialize() {
    const { queries } = this.props;
    this.queries = Object.keys(queries).map(name => {
      const mqList = window.matchMedia(queries[name]);

      // since addEventListener is not supported by Safari < 14, use deprecated method as a fallback
      if (mqList.addEventListener) {
        mqList.addEventListener('change', this.updateMatches);
      } else {
        mqList.addListener(this.updateMatches);
      }

      return { name, mqList };
    });
  }

  componentDidMount() {
    // If props.defaultMatches has been set, ensure we trigger a two-pass render.
    // This is useful for SSR with mismatching defaultMatches vs actual matches from window.matchMedia
    // Details: https://github.com/ReactTraining/react-media/issues/81
    if (this.props.defaultMatches !== undefined) {
      this.updateMatches();
    }
  }

  componentWillUnmount() {
    this.queries.forEach(({ mqList }) => {
      if (mqList.removeEventListener) {
        mqList.removeEventListener('change', this.updateMatches);
      } else {
        mqList.removeListener(this.updateMatches);
      }
    });
    this.queries = [];
  }

  render() {
    return this.props.children(this.state.matches);
  }
}

interface ThemedMediaProps {
  theme: ThemeProps;
  children: (matches: MatchesObject) => ReactNode | ReactNode[];
  defaultMatches?: Partial<MatchesObject>;
}

/**
 * Uses Media component to provide global media queries from
 * the theme object without the need to specify them
 * manually
 */
export const ThemedMedia = withTheme(({ theme, children, defaultMatches }: ThemedMediaProps) => {
  /**
   * WARNING: if the theme.breakpoints changes
   * please update this code
   */
  const [tablet, desktop, large_desktop] = theme.breakpoints;

  const queries = {
    tablet: `(min-width: ${tablet})`,
    desktop: `(min-width: ${desktop})`,
    large_desktop: `(min-width: ${large_desktop})`,
  };

  // Provide merged defaultMatches if they are
  // passed, but ONLY if they are passed. This
  // allows to avoid two-pass render if defaultMatches
  // are not provided
  //
  // Always merge provided with default, that
  // allows for less manual work when using defaultMatches
  const matches = defaultMatches
    ? {
        tablet: true,
        desktop: true,
        large_desktop: true,
        ...defaultMatches,
      }
    : undefined;

  return (
    <Media queries={queries} defaultMatches={matches}>
      {children}
    </Media>
  );
});

export default Media;
